import config from '../../../config/config.js';

/**
 * URL Builder utilities for Sport-Hub module (Client-side)
 * Centralizes URL construction logic for browser/client operations
 */

const getRuntimeGlobal = () =>
  typeof global !== 'undefined' ? global : typeof window !== 'undefined' ? window : null;

const getBrowserLocation = () =>
  typeof window !== 'undefined' && window.location ? window.location : null;

/**
 * Get the base URL from config or browser location
 * @returns {string} Base URL (e.g., "https://example.com")
 */
export const getBaseUrl = () => {
  try {
    const runtimeGlobal = getRuntimeGlobal();
    const runtimeProtocol = runtimeGlobal?.gConfig?.protocol;
    const runtimeHost = runtimeGlobal?.gConfig?.sections?.web?.hostname;

    if (runtimeProtocol && runtimeHost) {
      return `${runtimeProtocol}://${runtimeHost}`;
    }

    const cfg = config;

    if (cfg) {
      const configProtocol = cfg.protocol || 'http';
      const configHost = cfg.sections?.web?.hostname || cfg.hostname;

      if (configHost) {
        return `${configProtocol}://${configHost}`;
      }
    }

    const browserLocation = getBrowserLocation();

    if (browserLocation) {
      const { hostname, port, protocol } = browserLocation;

      if (
        (hostname === 'localhost' || hostname === '127.0.0.1') &&
        ['3000', '3030'].includes(port)
      ) {
        return `${protocol}//${hostname}:3020`;
      }

      return browserLocation.origin;
    }

    return '';
  } catch (e) {
    console.error('[SPORT-HUB urlBuilder] Error getting base URL:', e);
    const browserLocation = getBrowserLocation();
    if (browserLocation) {
      return browserLocation.origin;
    }
    return '';
  }
};

/**
 * Build shareable link for match (client-side)
 * @param {string} token - Match token
 * @returns {string|null} Full shareable URL or null if no token
 */
export const buildShareLink = (token) => {
  if (!token) {
    return null;
  }

  const base = getBaseUrl();
  return `${base}/public/sport-hub/match/${token}`;
};

/**
 * Build URL for match guest page
 * @param {Object} params
 * @param {string} params.matchToken - Match token
 * @returns {string|null} Full URL to match guest page
 */
export const buildMatchGuestUrl = ({ matchToken }) => {
  return buildShareLink(matchToken);
};

/**
 * Build base URL for Sport-Hub public structure pages.
 * @param {string} companyname - Company slug/name
 * @returns {string} Public base URL or empty string if companyname is missing
 */
export const buildSportHubPublicStructureBaseUrl = (companyname) => {
  if (!companyname) {
    return '';
  }

  const encodedCompanyname = encodeURIComponent(String(companyname));
  const path = `/public/sport-hub/${encodedCompanyname}`;
  const baseUrl = getBaseUrl();

  return baseUrl ? `${baseUrl}${path}` : path;
};

/**
 * Build URL for a Sport-Hub public structure page.
 * @param {Object} params
 * @param {string} params.companyname - Company slug/name
 * @param {string} params.structureId - Structure id
 * @returns {string} Full URL or empty string if required params are missing
 */
export const buildSportHubPublicStructureUrl = ({ companyname, structureId }) => {
  if (!companyname || !structureId) {
    return '';
  }

  const baseUrl = buildSportHubPublicStructureBaseUrl(companyname);
  return `${baseUrl}/${encodeURIComponent(String(structureId))}`;
};

export default {
  getBaseUrl,
  buildShareLink,
  buildMatchGuestUrl,
  buildSportHubPublicStructureBaseUrl,
  buildSportHubPublicStructureUrl,
};
